<!--
  PerColorApprovalBadge.vue -- Shared badge + "Request approval for
  this color" action for the per-color override surface.

  Decoration Model Refactor -- Phase P0.
  Card: Boulders #163 (https://trello.com/c/S5kN5poU)
  Plan: docs/planning/2026-06-22-decoration-refactor-execution-plan.md
  Decision: project_decoration-refactor-product-decisions.md #6

  Mounted from:
    * DecorationInspector.vue (product editor's per-decoration row)
    * DecorationDetailPage.vue (standalone detail page, near the
      per-client approval matrix)

  States
  ======

    | per_color_approval | status              | badge text                       |
    |--------------------|---------------------|----------------------------------|
    | null               | --                  | "Inherits approval"              |
    | set                | awaiting_approval   | "Pending color-specific approval"|
    | set                | approved            | "Approved for this color"        |
    | set                | changes_requested   | "Changes requested for color"    |
    | set                | rejected            | "Rejected for color"             |

  Action
  ======
  When the per-color override is NULL (inherits parent), the badge
  shows a button "Request approval for this color". Click POSTs to
  /api/placements/:id/request_per_color_approval. The badge is the
  smallest viable UI per locked decision #6 -- ship now, refine post-
  beta.

  URL migration (P5.5 PR-2c / PR-3b)
  ----------------------------------
  The endpoint root moved from /api/decorations/<id>/... to
  /api/placements/<id>/... when the SAFRS /api/decorations/ surface
  was retired. The badge's request prop is now ``placement-id``; the
  legacy ``decoration-id`` prop and the legacy URL no longer exist
  per the carte-blanche clean-break convention.

  Props
    * placement-id: int (required) -- the current placements.id this
        badge governs. Was ``decoration-id`` pre-PR-3b; renamed for
        accuracy with the post-refactor data model.
    * per-color-approval: object | null
        Shape: { id, status, ... } from the parent fetch; null when
        the Placement inherits its parent ApprovalRecord.
    * artwork-version-id: int | null -- passed to the request endpoint
        when the action fires. Optional in this component because
        higher-level pages often handle the request via a parent
        modal; when null, the button is hidden (mounting page is
        responsible for surfacing the "needs version" affordance).
    * client-id: int | null -- same shape as artwork-version-id.

  Emits
    * request-success(payload): { approval_record_id, ... }
        Parent re-renders the matrix / inspector row with the new
        override state.
    * request-error(message): user-visible string

  Style
    * data-testid="per-color-approval-badge-root"
    * Sub-testids: per-color-approval-badge-text,
      per-color-approval-badge-request-action,
      per-color-approval-badge-status-pill
-->
<template>
    <div
        class="per-color-approval-badge"
        data-testid="per-color-approval-badge-root"
    >
        <span
            class="per-color-approval-badge__text"
            data-testid="per-color-approval-badge-text"
        >
            {{ badgeText }}
        </span>
        <span
            v-if="statusPill"
            class="per-color-approval-badge__pill"
            :class="`per-color-approval-badge__pill--${statusPillClass}`"
            data-testid="per-color-approval-badge-status-pill"
        >
            {{ statusPill }}
        </span>
        <button
            v-if="canRequest"
            type="button"
            class="per-color-approval-badge__action"
            data-testid="per-color-approval-badge-request-action"
            :disabled="requesting"
            @click="requestApproval"
        >
            {{ requesting ? "Requesting..." : "Request approval for this color" }}
        </button>
    </div>
</template>

<script>
import api from "@/services/api";
import { safeApiError } from "@/utils/safeApiError";

export default {
    name: "PerColorApprovalBadge",
    props: {
        placementId: {
            // P5.5 PR-3b: was ``decorationId`` pre-rename. Accept null
            // for pending/unsaved placement rows; the request action
            // is gated on this being a positive int via canRequest,
            // so null cleanly suppresses the button.
            type: Number,
            default: null,
            validator: (v) => v === null || Number.isInteger(v),
        },
        perColorApproval: {
            type: Object,
            default: null,
        },
        artworkVersionId: {
            type: Number,
            default: null,
        },
        clientId: {
            type: Number,
            default: null,
        },
    },
    emits: ["request-success", "request-error"],
    data() {
        return {
            requesting: false,
        };
    },
    computed: {
        /**
         * The user-visible badge sentence.
         *
         * Strings are deliberately verbose so a screen reader reads
         * the full intent ("Approved for this color" rather than
         * "Approved"). Sentences match the state table in the
         * file-level comment.
         */
        badgeText() {
            if (!this.perColorApproval) {
                return "Inherits approval";
            }
            const status = this.perColorApproval.status;
            if (status === "approved") {
                return "Approved for this color";
            }
            if (status === "awaiting_approval") {
                return "Pending color-specific approval";
            }
            if (status === "changes_requested") {
                return "Changes requested for color";
            }
            if (status === "rejected") {
                return "Rejected for color";
            }
            if (status === "digitizing") {
                return "Digitizing color-specific approval";
            }
            // Unknown status -- show generic text so the badge stays
            // informative rather than blank.
            return "Color-specific approval";
        },
        /**
         * Small status chip beside the badge text. Lets the eye scan
         * by color for "approved" vs "awaiting" without parsing the
         * full sentence.
         */
        statusPill() {
            if (!this.perColorApproval) return null;
            const status = this.perColorApproval.status;
            if (status === "approved") return "Approved";
            if (status === "awaiting_approval") return "Pending";
            if (status === "changes_requested") return "Changes";
            if (status === "rejected") return "Rejected";
            if (status === "digitizing") return "Digitizing";
            return null;
        },
        statusPillClass() {
            if (!this.perColorApproval) return "default";
            const status = this.perColorApproval.status;
            if (status === "approved") return "approved";
            if (status === "awaiting_approval") return "pending";
            if (status === "changes_requested") return "warning";
            if (status === "rejected") return "danger";
            return "default";
        },
        canRequest() {
            // Show the action ONLY when inheriting AND we have a
            // persisted placement id AND version+client to ask
            // against. Higher-level pages pass nulls to suppress the
            // button (listing views, pending rows).
            return (
                !this.perColorApproval &&
                Number.isInteger(this.placementId) &&
                Number.isInteger(this.artworkVersionId) &&
                Number.isInteger(this.clientId)
            );
        },
    },
    methods: {
        async requestApproval() {
            this.requesting = true;
            try {
                // P5.5 PR-2c: URL root moved from /api/decorations/<id>/...
                // to /api/placements/<id>/... when the SAFRS legacy
                // surface was retired.
                //
                // PR-3b post-review: standardised on the ``api``
                // instance (``@/services/api``) so the baseURL is the
                // single source of truth -- previously this file used
                // raw ``axios.post('/api/placements/...')`` while the
                // sibling AddPlacementWizard used ``api.post('/placements/')``.
                // Same wire, one convention.
                const resp = await api.post(
                    `/placements/${this.placementId}/request_per_color_approval`,
                    {
                        artwork_version_id: this.artworkVersionId,
                        client_id: this.clientId,
                    }
                );
                this.$emit("request-success", resp.data);
            } catch (err) {
                // PR-3b post-review: route through safeApiError so we
                // never display backend error bodies verbatim (the
                // 401/403/404 cases get fixed labels; anything else
                // falls back to err.message and finally a generic
                // string).
                this.$emit(
                    "request-error",
                    safeApiError(err, "Request failed.")
                );
            } finally {
                this.requesting = false;
            }
        },
    },
};
</script>

<style scoped>
.per-color-approval-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border-radius: 4px;
    font-size: 0.8125rem;
}
.per-color-approval-badge__text {
    color: #374151;
}
.per-color-approval-badge__pill {
    padding: 0.0625rem 0.4rem;
    border-radius: 999px;
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    font-weight: 600;
    background: #d1d5db;
    color: #1f2937;
}
.per-color-approval-badge__pill--approved {
    background: #d1fae5;
    color: #065f46;
}
.per-color-approval-badge__pill--pending {
    background: #fef3c7;
    color: #92400e;
}
.per-color-approval-badge__pill--warning {
    background: #fde68a;
    color: #92400e;
}
.per-color-approval-badge__pill--danger {
    background: #fee2e2;
    color: #991b1b;
}
.per-color-approval-badge__action {
    background: none;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 0.1875rem 0.5rem;
    cursor: pointer;
    font-size: 0.75rem;
    color: #1d4ed8;
}
.per-color-approval-badge__action:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #93c5fd;
}
.per-color-approval-badge__action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
